/**
 * @file useComicDetailPage.js
 * @description 漫画详情页：详情、卷目录、评论、收藏、互动消息高亮定位
 * @module features/comic/composables/useComicDetailPage
 */

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { convertRouteParams } from '@/shared/utils/idHelper'
import { applyComicDetailSeo } from '@/shared/utils/seo'
import { fetchComicDetailApi } from '@/features/comic/api/comicApi'
import {
  cancelUserCollectApi,
  insertUserCollectApi,
} from '@/features/user/api/userApi'
import { VOLS_UNLOCKED_EVENT } from '@/shared/utils/authEvents'
import {
  canEnterReader,
  findVolByNum,
  getFirstFreeVolNum,
  isNeedLoginAccess,
  markVolsUnlocked,
  resolveVolAccessStatus,
} from '@/features/comic/utils/volAccess'
import { useComicDetailComments } from './useComicDetailComments'
import { openComicTip } from './useComicTip'

/**
 * @param {import('vue').Ref<string|number|null|undefined>} idRef
 * @param {(event: 'show-popup-message' | 'show-login-popup', ...args: unknown[]) => void} emit
 */
export function useComicDetailPage(idRef, emit) {
  const route = useRoute()
  const router = useRouter()
  const synopRef = ref(null)

  const state = reactive({

      comic: {}, // 漫画详情数据
      volTotal: 0, // 总卷数
      volInfoVos: [], // 卷列表（含 accessStatus）
      relatedComics: [], // 相关推荐
      readProgressVos: [], // 阅读进度记录
      isDescriptionExpanded: false, // 描述是否展开
      maxDescriptionLines: 4, // 最大显示行数
      chaptersPerPage: 20, // 每页显示的章节数（分组模式下未使用）
      loadedChapters: [], // 保留兼容，实际展示用 displayedChapters
      currentVolumeGroupIndex: 0, // 当前卷分组下标，每组最多 30 卷
      volumeGroupSize: 30, // 每组显示卷数
      buttonTop: 0, // 按钮的top位置
      activeTab: 'chapters', // 默认显示卷目录
      tabs: [],
      comments: [], // 评论列表
      commentPageNum: 1, // 评论当前页码
      commentPageSize: 5, // 评论每页数量（改为5条）
      commentTotal: 0, // 评论总数
      commentPages: 0, // 评论总页数
      loadingComments: false, // 是否正在加载评论
      commentContent: '', // 评论输入内容
      commentInputFocused: false, // 评论输入框是否已获得焦点（用于控制发布评论按钮显示）
      submittingComment: false, // 是否正在提交评论
      // 回复相关
      replyContentMap: {}, // 存储每条评论的回复内容 { messageId: '回复内容' }
      showReplyBoxMap: {}, // 存储每条评论的回复框显示状态 { messageId: true/false }
      submittingReplyMap: {}, // 存储每条评论/回复的提交状态 { messageId: true/false }
      loadingRepliesMap: {}, // 存储每条评论的回复加载状态 { messageId: true/false }
      pendingExpandRepliesMap: {}, // 存储每条主评论“准备展开且正在加载回复”的状态 { messageId: true/false }
      replyPageSize: 5, // 回复每页数量
      showAllRepliesMap: {}, // 存储每条评论是否展开所有回复 { messageId: true/false }
      likeEffectMap: {}, // 存储点赞特效状态 { messageId: true/false }
      activeFaq: null, // 当前展开的 FAQ ID
      faqs: [
        {
          id: 1,
          question: '如何购买更多章节？',
          answer: '您可以前往支付中心进行购买。'
        },
        {
          id: 2,
          question: '如何联系客服？',
          answer: '请通过首页的联系客服选项获取帮助。'
        }
      ],
      isCollect: false, // 收藏状态
      buttonText: '收藏', // 收藏按钮文本
      rewardButtonText: '打赏', // 打赏按钮文本
      token: '',
      localId: null, // 本地存储的漫画ID
      isDetailLoading: false // 标记是否正在加载或已加载详情
  })

  // alias for methods ported from Options API
  const ctx = state
  Object.assign(ctx, {
    get id() { return idRef.value },
    get $route() { return route },
    get $router() { return router },
    $nextTick: nextTick,
    $emit: emit,
    get $refs() { return { synopRef: synopRef.value } },
  })

  const bridge = {}
  useComicDetailComments(bridge, ctx)

  function loadDetailIfNeeded() {
      // 再次确认 localId（确保路由参数已准备好）
      if (!ctx.localId) {
        ctx.localId = ctx.id || route.params.id || null
      }

      // 如果还没有加载过详情数据，则加载
      if (!ctx.isDetailLoading && !ctx.comic.id && ctx.localId) {
        fetchComicDetail()
      }
    }
  function getPageBackgroundStyle() {
      return {
        backgroundImage: `url(${ctx.comic.cover})`
      }
    }
  async function fetchComicDetail() {
      // 防止重复请求
      if (ctx.isDetailLoading) {
                return
      }

      try {
        // 设置加载标志
        ctx.isDetailLoading = true

        // 清空之前的数据，避免重复加载
        ctx.loadedChapters = []
        ctx.currentPage = 1

        // 确保 id 参数正确传递
        // 优先使用 localId，如果没有则尝试从 props 或路由参数获取
        const comicId = ctx.localId || ctx.id || route.params.id
        if (!comicId) {
          console.error('漫画 ID 缺失，无法加载详情')
          ctx.isDetailLoading = false
          return
        }

        const idParam = String(comicId).trim()
        if (!idParam || idParam === 'null' || idParam === 'undefined') {
          console.error('漫画 ID 无效:', comicId)
          ctx.isDetailLoading = false
          return
        }

        const response = await fetchComicDetailApi(idParam, ctx.token || '')
        if (response.data.code === 200 || response.data.code === 0) {
          const { comicInfoVo, commendVo, readProgressVos, volInfoVos } = response.data.data
          // 更新漫画详情
          ctx.comic = comicInfoVo
          ctx.volTotal = comicInfoVo.volTotal // 设置总卷数
          ctx.volInfoVos = Array.isArray(volInfoVos) ? volInfoVos : []
          ctx.relatedComics = commendVo
          ctx.readProgressVos = readProgressVos || [] // 保存阅读进度记录

          // 处理主题数据：接口返回的字段名是 comicThemeVo，需要映射到 comicThemeArray
          // 支持多种可能的字段名：comicThemeVo, comicThemeArray, themeArray, themes
          if (comicInfoVo.comicThemeVo && Array.isArray(comicInfoVo.comicThemeVo)) {
            // 接口返回的字段名是 comicThemeVo
            ctx.comic.comicThemeArray = comicInfoVo.comicThemeVo
          } else if (comicInfoVo.comicThemeArray && Array.isArray(comicInfoVo.comicThemeArray)) {
            ctx.comic.comicThemeArray = comicInfoVo.comicThemeArray
          } else if (comicInfoVo.themeArray && Array.isArray(comicInfoVo.themeArray)) {
            ctx.comic.comicThemeArray = comicInfoVo.themeArray
          } else if (comicInfoVo.themes && Array.isArray(comicInfoVo.themes)) {
            ctx.comic.comicThemeArray = comicInfoVo.themes
          } else {
            // 如果没有找到主题数据，设置为空数组
            ctx.comic.comicThemeArray = []
          }

          // 确保 comicThemeArray 是数组格式
          if (!Array.isArray(ctx.comic.comicThemeArray)) {
            ctx.comic.comicThemeArray = []
          }

          ctx.isCollect = ctx.comic.isCollect // 根据接口状态初始化收藏状态
          ctx.buttonText = ctx.isCollect ? '取消收藏' : '收藏'
          ctx.rewardButtonText = '打赏'
          ctx.currentVolumeGroupIndex = 0 // 重置为第一组
          // 初始化加载第一页章节（兼容旧逻辑，展示由 displayedChapters 计算属性提供）
          ctx.loadedChapters = ctx.volTotal ? Array.from({ length: Math.min(ctx.volumeGroupSize, ctx.volTotal) }, (_, i) => i + 1) : []
          // 评论区：从互动消息带 highlightComment 时需先切到评论区再分页定位
          if (route.query.highlightComment) {
            ctx.activeTab = 'comments'
            nextTick(() => {
              bridge.scrollToHighlightCommentFromQuery()
            })
          } else if (ctx.comments.length === 0) {
            bridge.fetchComments()
          }
          applyComicDetailSeo(ctx.comic)
          // 等待DOM更新后计算按钮位置
          nextTick(() => {
            updateButtonPosition()
          })
        } else {
          console.error('获取漫画详情失败', response.data)
        }
      } catch (error) {
        console.error('请求错误:', error)
      } finally {
        // 重置加载标志
        ctx.isDetailLoading = false
      }
    }
  function toggleDescription() {
      ctx.isDescriptionExpanded = !ctx.isDescriptionExpanded
      // 等待DOM更新后计算按钮位置
      nextTick(() => {
        updateButtonPosition()
      })
    }
  function updateButtonPosition() {
      if (!synopRef.value) return

      const synopElement = synopRef.value
      const lineHeight = parseFloat(window.getComputedStyle(synopElement).lineHeight)
      const fontSize = parseFloat(window.getComputedStyle(synopElement).fontSize)

      if (ctx.isDescriptionExpanded) {
        // 展开状态：按钮在最后一行
        const totalHeight = synopElement.offsetHeight
        // 按钮位置 = 总高度 - 半行高度（让按钮垂直居中在最后一行）
        ctx.buttonTop = totalHeight - lineHeight / 2
      } else {
        // 折叠状态：按钮在第三行
        // 第三行中间位置 = 前两行高度 + 第三行的一半高度
        ctx.buttonTop = lineHeight * 2 + lineHeight / 2
      }
    }
  function loadNextPage() {
      if (ctx.loadedChapters.length >= ctx.volTotal) return

      const start = ctx.loadedChapters.length + 1
      const end = Math.min(start + ctx.chaptersPerPage - 1, ctx.volTotal)
      const newChapters = Array.from(
        { length: end - start + 1 },
        (_, i) => start + i
      )

      ctx.loadedChapters = [...ctx.loadedChapters, ...newChapters]
      ctx.currentPage++
    }
  function goToReadingPage(volOrNum) {
      const volNum = typeof volOrNum === 'object' && volOrNum != null
        ? volOrNum.num
        : volOrNum
      if (isNeedLoginVolume(volNum)) {
        emit('show-login-popup')
        return
      }
      if (!canEnterVolume(volNum)) {
        emit('show-login-popup')
        return
      }

      // 查找该卷的阅读进度
      const volNumNum = Number(volNum)
      const progress = ctx.readProgressVos.find(p => Number(p.volNum) === volNumNum)

      // 构建查询参数
      const query = {}
      if (progress && progress.pageIndex !== undefined) {
        // 如果有进度，传递 pageIndex 和 restoreProgress 参数
        query.pageIndex = progress.pageIndex
        query.restoreProgress = 'true' // 明确指示需要恢复进度
              }

      // 跳转到阅读页面
      router.push({
        name: 'ComicReader',
        params: convertRouteParams({
          comicId: ctx.comic.id,
          volNum: volNum
        }),
        query: query
      })
    }
    // 跳转到开始观看/继续观看
  function goToReadOrContinue() {
      const firstVolNum = getFirstFreeVolNum(ctx.volInfoVos)
      if (ctx.readProgressVos && ctx.readProgressVos.length > 0) {
        const sortedProgress = [...ctx.readProgressVos].sort((a, b) => a.volNum - b.volNum)
        const lastProgress = sortedProgress[sortedProgress.length - 1]
        if (isNeedLoginVolume(lastProgress.volNum)) {
          emit('show-login-popup')
          return
        }
        if (!canEnterVolume(lastProgress.volNum)) {
          emit('show-login-popup')
          return
        }
        router.push({
          name: 'ComicReader',
          params: convertRouteParams({
            comicId: ctx.comic.id,
            volNum: lastProgress.volNum
          }),
          query: {
            pageIndex: lastProgress.pageIndex
          }
        })
        return
      }
      router.push({
        name: 'ComicReader',
        params: convertRouteParams({
          comicId: ctx.comic.id,
          volNum: firstVolNum
        })
      })
    }
    // 判断是否有阅读记录
  function hasReadProgress() {
      return ctx.readProgressVos && ctx.readProgressVos.length > 0
    }
    // 判断指定卷号是否有阅读记录
  function hasVolumeProgress(volNum) {
      if (!ctx.readProgressVos || ctx.readProgressVos.length === 0) {
        return false
      }
      // 确保 volNum 类型匹配（可能是数字或字符串）
      const volNumNum = Number(volNum)
      return ctx.readProgressVos.some(p => Number(p.volNum) === volNumNum)
    }
    // 获取指定卷号的阅读进度（0-1之间的小数）
  function getVolumeProgress(volNum) {
      if (!ctx.readProgressVos || ctx.readProgressVos.length === 0) {
        return 0
      }
      const volNumNum = Number(volNum)
      const progress = ctx.readProgressVos.find(p => Number(p.volNum) === volNumNum)
      const progressValue = progress ? (progress.progress || 0) : 0
            return progressValue
    }
  function getVolumeAccess(volumeNumber) {
      const firstVolNum = getFirstFreeVolNum(ctx.volInfoVos)
      const vol = findVolByNum(ctx.volInfoVos, volumeNumber) || { num: volumeNumber }
      return resolveVolAccessStatus(vol, { hasToken: Boolean(ctx.token), firstVolNum })
    }

  function isNeedLoginVolume(volumeNumber) {
      return isNeedLoginAccess(getVolumeAccess(volumeNumber))
    }

  function canEnterVolume(volumeNumber) {
      return canEnterReader(getVolumeAccess(volumeNumber))
    }

    // NEED_LOGIN 置灰；LOCKED 可进阅读器
  function isVolumeRestricted(volumeNumber) {
      return isNeedLoginVolume(volumeNumber)
    }

  function goToComicDetail(comicId) {
      router.push({ 
        name: 'ComicDetail', 
        params: convertRouteParams({ id: comicId }) 
      })
    }
  function toggleAnswer(id) {
      ctx.activeFaq = ctx.activeFaq === id ? null : id // 切换展开状态
    }
    // 处理收藏/取消收藏逻辑
  function ensureToken() {
      if (!ctx.token) {
        ctx.token = JSON.parse(localStorage.getItem('user'))?.token || ''
      }
    }
  async function handleCollect() {
    ensureToken()
    if (!ctx.token) {
      ctx.buttonText = '请登录后收藏'
      return
    }
    const comicId = ctx.comic.id
    try {
      if (!ctx.isCollect) {
        const response = await insertUserCollectApi(comicId, ctx.token)
        if (response.data.code === 10474) {
          ctx.buttonText = '请登录后收藏'
          return
        }
        if (response.data.code === 4002 || response.data.code === 200 || response.data.code === 0) {
          ctx.isCollect = true
          ctx.buttonText = '已收藏'
        }
        if (response.data.code === 200 || response.data.code === 0) {
          ctx.isCollect = true
          ctx.buttonText = '收藏成功'
        }
      } else {
        const response = await cancelUserCollectApi(comicId, ctx.token)
        if (response.data.code === 200 || response.data.code === 0) {
          ctx.isCollect = false
          ctx.buttonText = '已取消收藏'
        }
      }
    } catch (error) {
      console.error('操作失败:', error)
    }
  }

  function handleReward() {
    ensureToken()
    if (!ctx.token) {
      emit('show-login-popup')
      return
    }
    const comicId = ctx.comic?.id ?? ctx.id
    if (!comicId) return
    openComicTip({
      comicId: String(comicId),
      comicTitle: ctx.comic?.name || '',
      source: 'detail',
    })
  }

  const shouldShowToggleButton = computed(() => {
    if (!state.comic.synop) return false
    const estimatedCharsPerLine = 50
    const maxChars = estimatedCharsPerLine * 3
    return state.comic.synop.length > maxChars
  })

  const volumeGroups = computed(() => {
    const size = state.volumeGroupSize || 30
    if (state.volInfoVos.length) {
      const nums = state.volInfoVos
        .map((vol) => Number(vol.num))
        .filter((n) => Number.isFinite(n))
      if (!nums.length) return []
      const minNum = Math.min(...nums)
      const maxNum = Math.max(...nums)
      const list = []
      for (let start = minNum; start <= maxNum; start += size) {
        const end = Math.min(start + size - 1, maxNum)
        list.push({ label: `${start}-${end}`, start, end })
      }
      return list
    }
    const total = state.volTotal || 0
    if (total <= 0) return []
    const list = []
    for (let start = 1; start <= total; start += size) {
      const end = Math.min(start + size - 1, total)
      list.push({ label: `${start}-${end}`, start, end })
    }
    return list
  })

  const displayedChapters = computed(() => {
    const groups = volumeGroups.value
    const idx = state.currentVolumeGroupIndex
    if (!groups.length || idx < 0 || idx >= groups.length) return []
    const { start, end } = groups[idx]
    if (state.volInfoVos.length) {
      return state.volInfoVos
        .filter((vol) => {
          const num = Number(vol.num)
          return num >= start && num <= end
        })
        .sort((a, b) => Number(a.num) - Number(b.num))
    }
    return Array.from({ length: end - start + 1 }, (_, i) => ({ num: start + i }))
  })

  function initAndLoadDetail() {
    if (!ctx.localId) {
      ctx.localId = ctx.id || route.params.id || null
      if (!ctx.localId) {
        console.error('initAndLoadDetail - 漫画 ID 缺失，无法加载详情')
        console.error('Props id:', ctx.id, 'Route params:', route.params)
        setTimeout(() => {
          ctx.localId = ctx.id || route.params.id || null
          if (ctx.localId) {
                        loadDetailIfNeeded()
          } else {
            console.error('延迟后仍然无法获取 ID')
          }
        }, 100)
        return
      }
          }
    nextTick(() => {
      loadDetailIfNeeded()
    })
  }

  watch(
    () => route.params.id,
    (newId, oldId) => {
      if (!route.params) return
      const resolvedNew = newId || idRef.value
      const resolvedOld = oldId
      if (resolvedNew && resolvedNew !== resolvedOld) {
        ctx.localId = idRef.value || route.params.id || null
        if (!ctx.localId) {
          console.error('路由变化 - 漫画 ID 缺失，无法加载详情')
          return
        }
                ctx.isDetailLoading = false
        nextTick(() => {
          if (!ctx.localId) {
            ctx.localId = idRef.value || route.params.id || null
          }
          if (ctx.localId) {
            fetchComicDetail()
          }
        })
        bridge.resetCommentsState()
      }
    }
  )

  watch(idRef, (newId) => {
    if (newId && newId !== ctx.localId) {
      ctx.localId = newId
            ctx.isDetailLoading = false
      fetchComicDetail()
      bridge.resetCommentsState()
    }
  })

  function onVolsUnlocked(event) {
    const comicId = event?.detail?.comicId
    if (comicId && String(comicId) !== String(ctx.comic?.id || ctx.localId)) return
    markVolsUnlocked(ctx.volInfoVos, event?.detail?.unlockedVolIds || [])
  }

  onMounted(() => {
    initAndLoadDetail()
    nextTick(() => {
      updateButtonPosition()
      window.addEventListener('resize', updateButtonPosition)
    })
    window.addEventListener(VOLS_UNLOCKED_EVENT, onVolsUnlocked)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updateButtonPosition)
    window.removeEventListener(VOLS_UNLOCKED_EVENT, onVolsUnlocked)
  })

  // created
  state.token = JSON.parse(localStorage.getItem('user'))?.token || ''
  state.localId = idRef.value || route.params.id || null

  const {
    handleCommentPageChange,
    submitComment,
    toggleReplyBox,
    submitCommentReply,
    toggleCommentReplies,
    toggleShowAllReplies,
    deleteComicComment,
    deleteComicReply,
    likeComment,
    likeReply,
  } = bridge

  return {
    synopRef,
    comic: computed(() => state.comic),
    relatedComics: computed(() => state.relatedComics),
    readProgressVos: computed(() => state.readProgressVos),
    isDescriptionExpanded: computed({
      get: () => state.isDescriptionExpanded,
      set: (v) => { state.isDescriptionExpanded = v },
    }),
    buttonTop: computed({
      get: () => state.buttonTop,
      set: (v) => { state.buttonTop = v },
    }),
    currentVolumeGroupIndex: computed({
      get: () => state.currentVolumeGroupIndex,
      set: (v) => { state.currentVolumeGroupIndex = v },
    }),
    comments: computed(() => state.comments),
    commentPageNum: computed(() => state.commentPageNum),
    commentPageSize: computed(() => state.commentPageSize),
    commentTotal: computed(() => state.commentTotal),
    commentPages: computed(() => state.commentPages),
    loadingComments: computed(() => state.loadingComments),
    commentContent: computed({
      get: () => state.commentContent,
      set: (v) => { state.commentContent = v },
    }),
    commentInputFocused: computed({
      get: () => state.commentInputFocused,
      set: (v) => { state.commentInputFocused = v },
    }),
    submittingComment: computed(() => state.submittingComment),
    replyContentMap: state.replyContentMap,
    showReplyBoxMap: state.showReplyBoxMap,
    submittingReplyMap: state.submittingReplyMap,
    pendingExpandRepliesMap: state.pendingExpandRepliesMap,
    showAllRepliesMap: state.showAllRepliesMap,
    likeEffectMap: state.likeEffectMap,
    isCollect: computed(() => state.isCollect),
    buttonText: computed(() => state.buttonText),
    rewardButtonText: computed(() => state.rewardButtonText),
    shouldShowToggleButton,
    volumeGroups,
    displayedChapters,
    getPageBackgroundStyle,
    toggleDescription,
    goToReadingPage,
    goToReadOrContinue,
    hasReadProgress,
    hasVolumeProgress,
    getVolumeProgress,
    isVolumeRestricted,
    getVolumeAccess,
    goToComicDetail,
    handleCollect,
    handleReward,
    handleCommentPageChange,
    submitComment,
    toggleReplyBox,
    submitCommentReply,
    toggleCommentReplies,
    toggleShowAllReplies,
    deleteComicComment,
    deleteComicReply,
    likeComment,
    likeReply,
  }
}
